import React from 'react';
import {
  AppstoreOutlined,
  CalendarOutlined,
  CheckCircleOutlined,
  CheckOutlined,
  CheckSquareOutlined,
  ClockCircleOutlined,
  EditOutlined,
  ExpandAltOutlined,
  FileTextOutlined,
  FormOutlined,
  LoadingOutlined,
  MessageOutlined,
  PlusCircleOutlined,
  PlusOutlined,
  RobotOutlined,
  ThunderboltOutlined,
  UserOutlined,
} from '@ant-design/icons';
import clsx from 'clsx';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router';
import { Button, DatePicker, Input, Select, Spin, Tag } from 'antd';
import { useEffectWithPrev } from '@owl-frontend/components';
import { generateArtifact as generateArtifactApi } from '../../api/artifacts/client';
import type { Artifact } from '../../api/artifacts/interface';
import type { ClaimFile } from '../../api/files/interface';
import type {
  Task,
  TaskActivity,
  TaskLink,
  TaskPriority,
  TaskStreamEntry,
  TaskSuggestion,
} from '../../api/tasks/interface';
import { globalBus } from '../../context/event-bus';
import { injectArtifactAction } from '../../data/artifacts/logic';
import useArtifactsHook from '../../data/artifacts/useArtifactsHook';
import useEventsHook from '../../data/events/useEventsHook';
import { NOTIFICATION_EMIT_EVENTS } from '../../data/notifications/logic';
import { useSelector } from '../../data/store';
import { useTaskHook } from '../../data/tasks/useTasksHook';
import MentionInput, { type MentionInputHandle } from '../Chat/MentionInput';
import {
  type MentionClickHandler,
  renderUserText,
} from '../Chat/MessageBubble';
import { useEscDossierContext } from '../DossierContext';
import { processCitations } from '../extractCitedFiles';
import { EscMarkdown } from '../Markdown/Markdown';
import ReferencesCard from '../ReferencesCard/ReferencesCard';
import { useCitationHandler } from '../utils';
import type { TagCount } from './grouping';
import styles from './TaskDetail.module.scss';

dayjs.extend(utc);

interface Props {
  taskId: string;
}

// Inline tag editor with autocomplete: suggests tags already used across the
// workspace (with usage counts) and offers to create the typed tag when it
// isn't an exact existing match. Stays open after adding so several tags can be
// added in a row.
const TagAutocomplete: React.FC<{
  known: TagCount[];
  existing: string[];
  onAdd: (tag: string) => void;
}> = ({ known, existing, onAdd }) => {
  const [adding, setAdding] = React.useState(false);
  const [value, setValue] = React.useState('');
  const [hi, setHi] = React.useState(0);
  const blurTimer = React.useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined
  );

  const q = value.trim().toLowerCase();
  // Tags are canonically lowercased on add, but pre-existing tags may be mixed
  // case, so match and dedupe case-insensitively throughout.
  const existingLower = React.useMemo(
    () => existing.map((t) => t.toLowerCase()),
    [existing]
  );
  const available = React.useMemo(
    () => known.filter((e) => !existingLower.includes(e.tag.toLowerCase())),
    [known, existingLower]
  );
  const matches = React.useMemo(
    () =>
      q ? available.filter((e) => e.tag.toLowerCase().includes(q)) : available,
    [available, q]
  );
  const exact =
    available.some((e) => e.tag.toLowerCase() === q) ||
    existingLower.includes(q);
  const showCreate = Boolean(q) && !exact;
  const optionCount = matches.length + (showCreate ? 1 : 0);

  React.useEffect(() => () => clearTimeout(blurTimer.current), []);

  const close = () => {
    setValue('');
    setHi(0);
    setAdding(false);
  };

  const apply = (index: number) => {
    const isCreate = showCreate && index === matches.length;
    const tag = (isCreate ? q : matches[index]?.tag ?? q).trim().toLowerCase();
    if (tag) {
      onAdd(tag);
    }
    // Keep the editor open so several tags can be added in a row.
    setValue('');
    setHi(0);
  };

  if (!adding) {
    return (
      <button
        type="button"
        className={styles.tagAdd}
        onClick={() => setAdding(true)}
      >
        <PlusOutlined /> Tag
      </button>
    );
  }

  return (
    <span className={styles.tagCombo}>
      <Input
        size="small"
        autoFocus
        variant="borderless"
        className={styles.tagInput}
        placeholder="Add tag…"
        value={value}
        onChange={(e) => {
          setValue(e.target.value);
          setHi(0);
        }}
        onFocus={() => clearTimeout(blurTimer.current)}
        onBlur={() => {
          blurTimer.current = setTimeout(close, 130);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            if (optionCount) {
              apply(hi);
            } else if (q) {
              apply(0);
            }
          } else if (e.key === 'ArrowDown') {
            e.preventDefault();
            setHi((h) => Math.min(h + 1, Math.max(optionCount - 1, 0)));
          } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setHi((h) => Math.max(h - 1, 0));
          } else if (e.key === 'Escape') {
            e.preventDefault();
            close();
          }
        }}
      />
      {optionCount ? (
        <div className={styles.tagMenu}>
          {matches.map((e, i) => (
            <button
              type="button"
              key={e.tag}
              className={`${styles.tagOpt} ${i === hi ? styles.tagOptHi : ''}`}
              onMouseEnter={() => setHi(i)}
              onMouseDown={(ev) => {
                ev.preventDefault();
                clearTimeout(blurTimer.current);
                apply(i);
              }}
            >
              <span className={styles.tagOptName}>{e.tag}</span>
              <span className={styles.tagOptCount}>{e.count}</span>
            </button>
          ))}
          {showCreate ? (
            <button
              type="button"
              className={`${styles.tagOpt} ${styles.tagOptCreate} ${
                hi === matches.length ? styles.tagOptHi : ''
              }`}
              onMouseEnter={() => setHi(matches.length)}
              onMouseDown={(ev) => {
                ev.preventDefault();
                clearTimeout(blurTimer.current);
                apply(matches.length);
              }}
            >
              <PlusOutlined />
              <span className={styles.tagOptName}>Create “{q}”</span>
            </button>
          ) : null}
        </div>
      ) : null}
    </span>
  );
};

const LINK_ICON: Record<TaskLink['type'], React.ReactNode> = {
  task: <CheckSquareOutlined />,
  artifact: <AppstoreOutlined />,
  note: <FileTextOutlined />,
};

const PRIORITY_COLOR: Record<TaskPriority, string> = {
  high: '#d4380d',
  med: 'var(--app-color-primary)',
  low: 'var(--app-color-border-secondary)',
};

const PRIORITY_OPTIONS: {
  value: TaskPriority;
  label: React.ReactNode;
}[] = (['high', 'med', 'low'] as TaskPriority[]).map((p) => ({
  value: p,
  label: (
    <span className={styles.prioOption}>
      <span
        className={styles.prioDot}
        style={{ background: PRIORITY_COLOR[p] }}
      />
      {p === 'high' ? 'High' : p === 'med' ? 'Medium' : 'Low'}
    </span>
  ),
}));

// Serialized composer output keeps its @task_id:/@file_id: tokens inline so
// the posted comment renders them as chips (same as chat). We also collect the
// bare ids for the backend's mentions list.
function parseMentions(serialized: string): {
  text: string;
  mentions: string[];
} {
  const mentions: string[] = [];
  const re = /@(?:task_id|file_id):([\w-]+)/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(serialized)) !== null) {
    mentions.push(m[1]);
  }
  return { text: serialized.trim(), mentions };
}

const authorLabel = (author: string) => (author === 'agent' ? 'Owl' : author);
const isOwlAuthor = (author: string) => author === 'agent' || author === 'Owl';

const fmtWhen = (iso: string | null) =>
  iso ? dayjs.utc(iso).local().format('MMM D · h:mm A') : '';

interface EntryProps {
  entry: TaskStreamEntry;
  files: ClaimFile[];
  tasks: Task[];
  onFileClick: (fileId: string, page?: number) => void;
  onKnowledgeClick: (path: string, anchor?: string) => void;
  onWikiClick: (path: string) => void;
  onMentionClick: MentionClickHandler;
}

// Owl-authored bodies carry citations (rendered as markdown); human-authored
// bodies carry @-mention tokens (rendered as chips, same as chat).
const EntryBody: React.FC<{
  entry: TaskStreamEntry;
  files: ClaimFile[];
  tasks: Task[];
  onFileClick: (fileId: string, page?: number) => void;
  onKnowledgeClick: (path: string, anchor?: string) => void;
  onWikiClick: (path: string) => void;
  onMentionClick: MentionClickHandler;
  className: string;
}> = ({
  entry,
  files,
  tasks,
  onFileClick,
  onKnowledgeClick,
  onWikiClick,
  onMentionClick,
  className,
}) => {
  const handleCitationClick = useCitationHandler(onFileClick, onKnowledgeClick);
  const { renumberedText } = React.useMemo(
    () => processCitations(entry.text),
    [entry.text]
  );
  if (isOwlAuthor(entry.author)) {
    return (
      <div className={className}>
        <EscMarkdown
          text={renumberedText || entry.text || ''}
          customCitationHandler={handleCitationClick}
          onPageLink={onWikiClick}
          hidePage
          stateDb
        />
      </div>
    );
  }
  return (
    <div className={className}>
      {renderUserText(entry.text || '', files, tasks, onMentionClick)}
    </div>
  );
};

// A task-local comment: plain author + time + body (with @-mention chips).
const CommentEntry: React.FC<EntryProps> = ({
  entry,
  files,
  tasks,
  onFileClick,
  onKnowledgeClick,
  onWikiClick,
  onMentionClick,
}) => (
  <div className={styles.comment}>
    <div className={styles.commentHead}>
      <span className={styles.commentAuthor}>{authorLabel(entry.author)}</span>
      <span className={styles.commentTime}>{fmtWhen(entry.created_at)}</span>
    </div>
    <EntryBody
      entry={entry}
      files={files}
      tasks={tasks}
      onFileClick={onFileClick}
      onKnowledgeClick={onKnowledgeClick}
      onWikiClick={onWikiClick}
      onMentionClick={onMentionClick}
      className={styles.commentText}
    />
  </div>
);

// A claim note: a permanent-record entry, visually distinct (purple) from an
// ephemeral comment. Long notes clamp and expand inline; the icon opens the
// full note in its own viewer. Notes have no status.
const NoteEntry: React.FC<EntryProps> = ({
  entry,
  files,
  tasks,
  onFileClick,
  onKnowledgeClick,
  onWikiClick,
  onMentionClick,
}) => {
  const [open, setOpen] = React.useState(false);
  const fromOwl = isOwlAuthor(entry.author);
  const long = (entry.text || '').length > 180;
  return (
    <div className={styles.noteItem}>
      <div className={styles.noteHead}>
        <span className={styles.noteAuthor}>{authorLabel(entry.author)}</span>
        <span className={clsx(styles.noteChip, fromOwl && styles.noteChipOwl)}>
          {fromOwl ? <RobotOutlined /> : <FormOutlined />} Claim note
        </span>
        <span className={styles.noteTime}>{fmtWhen(entry.created_at)}</span>
        {entry.note && (
          <button
            type="button"
            className={styles.noteOpen}
            title="Open full note"
            onClick={() => onFileClick(entry.note!)}
          >
            <ExpandAltOutlined />
          </button>
        )}
      </div>
      <EntryBody
        entry={entry}
        files={files}
        tasks={tasks}
        onFileClick={onFileClick}
        onKnowledgeClick={onKnowledgeClick}
        onWikiClick={onWikiClick}
        onMentionClick={onMentionClick}
        className={clsx(styles.noteBody, open && styles.noteBodyOpen)}
      />
      {long && (
        <button
          type="button"
          className={styles.noteMore}
          onClick={() => setOpen((v) => !v)}
        >
          {open ? 'Show less' : 'Show more'}
        </button>
      )}
    </div>
  );
};

const DiscussionEntry: React.FC<EntryProps> = (props) =>
  props.entry.kind === 'note' ? (
    <NoteEntry {...props} />
  ) : (
    <CommentEntry {...props} />
  );

const ACTIVITY_ICON: Record<string, React.ReactNode> = {
  create: <PlusCircleOutlined />,
  check: <CheckCircleOutlined />,
  clock: <ClockCircleOutlined />,
  note: <FileTextOutlined />,
  comment: <MessageOutlined />,
  artifact: <AppstoreOutlined />,
};

// A row in the lifecycle activity trail (below the discussion). Note-derived
// rows link to open the underlying note.
const ActivityRow: React.FC<{
  activity: TaskActivity;
  onFileClick: (fileId: string, page?: number) => void;
}> = ({ activity, onFileClick }) => (
  <div className={styles.activityRow}>
    <span className={styles.activityIco}>
      {ACTIVITY_ICON[activity.kind] ?? <FileTextOutlined />}
    </span>
    <span className={styles.activityText}>
      {activity.note ? (
        <button
          type="button"
          className={styles.activityLink}
          onClick={() => onFileClick(activity.note!)}
        >
          {activity.text}
        </button>
      ) : (
        activity.text
      )}
      {activity.who ? <strong> {authorLabel(activity.who)}</strong> : null}
    </span>
    <span className={styles.activityWhen}>{fmtWhen(activity.ts)}</span>
  </div>
);

const TaskDetail: React.FC<Props> = ({ taskId }) => {
  const { dossierId } = useParams<{ dossierId: string }>();
  const { onFileClick, onTaskClick, onArtifactClick, onWikiClick } =
    useEscDossierContext();
  const dispatch = useDispatch();
  const onKnowledgeClick = React.useCallback(
    (kbPath: string, anchor?: string) =>
      onWikiClick(`knowledge/${kbPath}${anchor ? `#${anchor}` : ''}`),
    [onWikiClick]
  );
  const [{ task, taskActions }, handler] = useTaskHook(dossierId ?? '', taskId);

  // Suggested-step generation is tracked through the artifacts + their events:
  // an artifact linked to this task (by task_id + schema) is the step's output,
  // and its event status drives the card icon (spinner → checkmark).
  const [{ artifacts }, artifactsHandler] = useArtifactsHook(dossierId ?? '');
  const [{ events }, eventsHandler] = useEventsHook(dossierId ?? '');
  // Optimistic per-schema generating flags: set the instant a card is clicked
  // (before the create request resolves) and cleared once the artifact is
  // observed ready, so the spinner appears immediately and doesn't flicker back
  // to the create icon while a completed event's data is still being refetched.
  const [generating, setGenerating] = React.useState<Set<string>>(
    () => new Set()
  );
  const suggestFetchedRef = React.useRef<string | null>(null);
  React.useEffect(() => {
    if (!dossierId || suggestFetchedRef.current === dossierId) {
      return;
    }
    suggestFetchedRef.current = dossierId;
    artifactsHandler.fetchArtifacts();
    eventsHandler.fetchEvents();
  }, [dossierId, artifactsHandler, eventsHandler]);
  React.useEffect(() => {
    if (!dossierId) {
      return;
    }
    const refetch = (data: any) => {
      if (data?.dossierId === dossierId) {
        artifactsHandler.fetchArtifacts();
        eventsHandler.fetchEvents();
        // The agent records a "Created <artifact>" activity on completion, so
        // refetch the task to surface it in the activity trail.
        handler.fetchTask();
      }
    };
    const onFailed = (data: any) => {
      // Genuine failure: drop optimistic spinners so the card reverts to create.
      if (data?.dossierId === dossierId) {
        setGenerating(new Set());
      }
      refetch(data);
    };
    globalBus.on(NOTIFICATION_EMIT_EVENTS.EventProcessed, refetch);
    globalBus.on(NOTIFICATION_EMIT_EVENTS.EventProcessingFailed, onFailed);
    return () => {
      globalBus.off(NOTIFICATION_EMIT_EVENTS.EventProcessed, refetch);
      globalBus.off(NOTIFICATION_EMIT_EVENTS.EventProcessingFailed, onFailed);
    };
  }, [dossierId, artifactsHandler, eventsHandler, handler]);

  const eventStatusById = React.useMemo(
    () => new Map(events.map((e) => [e.event_id, e.status])),
    [events]
  );
  const artifactStatus = React.useCallback(
    (a: Artifact): 'processing' | 'failed' | 'ready' => {
      if (!a.event_id) {
        return 'ready';
      }
      const st = eventStatusById.get(a.event_id);
      if (st === 'completed') {
        return Object.keys(a.data ?? {}).length > 0 ? 'ready' : 'failed';
      }
      if (st === undefined || st === 'pending' || st === 'processing') {
        return 'processing';
      }
      return 'failed';
    },
    [eventStatusById]
  );
  const suggestionArtifact = React.useCallback(
    (schemaName: string): Artifact | undefined =>
      artifacts
        .filter((a) => a.task_id === taskId && a.schema_name === schemaName)
        .sort((a, b) =>
          (b.created_at ?? '').localeCompare(a.created_at ?? '')
        )[0],
    [artifacts, taskId]
  );
  // Clear an optimistic flag once its artifact is actually ready (the event has
  // completed and the refetched artifact carries data).
  React.useEffect(() => {
    setGenerating((prev) => {
      if (prev.size === 0) {
        return prev;
      }
      const next = new Set(prev);
      prev.forEach((schemaName) => {
        const a = suggestionArtifact(schemaName);
        if (a && artifactStatus(a) === 'ready') {
          next.delete(schemaName);
        }
      });
      return next.size === prev.size ? prev : next;
    });
  }, [suggestionArtifact, artifactStatus]);
  const runSuggestion = React.useCallback(
    async (s: TaskSuggestion) => {
      if (!dossierId || s.type !== 'generate_artifact') {
        return;
      }
      setGenerating((prev) => new Set(prev).add(s.schema_name));
      try {
        const artifact = await generateArtifactApi({
          dossierId,
          schemaName: s.schema_name,
          taskId,
        });
        dispatch(injectArtifactAction(dossierId, artifact));
      } catch {
        setGenerating((prev) => {
          const next = new Set(prev);
          next.delete(s.schema_name);
          return next;
        });
      }
    },
    [dossierId, taskId, dispatch]
  );

  const currentUser = useSelector((s) => s.session.current);

  const roster = React.useMemo(() => {
    const self =
      currentUser?.full_name?.trim() || currentUser?.email || undefined;
    return self ? [self] : [];
  }, [currentUser]);

  const fetching = taskActions.fetchTask.status === 'pending';
  const commenting = taskActions.commentOnTask.status === 'pending';

  // Cache-aware auto-fetch: gate on per-id presence, not the slice-wide status.
  React.useEffect(() => {
    if (!task) {
      handler.fetchTask();
    }
  }, [task, handler]);

  // Refresh the workspace-wide tag list whenever a task is opened, so the tag
  // autocomplete suggests tags used across all dossiers (not just this one).
  React.useEffect(() => {
    handler.fetchAllTags();
  }, [taskId, handler]);

  const [title, setTitle] = React.useState('');
  const [description, setDescription] = React.useState('');
  const [editingTitle, setEditingTitle] = React.useState(false);
  const [editingDesc, setEditingDesc] = React.useState(false);

  React.useEffect(() => {
    if (task) {
      setTitle(task.title);
      setDescription(task.description ?? '');
    }
  }, [task]);

  const mentionInputRef = React.useRef<MentionInputHandle>(null);
  const commentTasks = useSelector((s) =>
    dossierId ? s.tasks.tasks[dossierId] ?? [] : []
  );
  // Workspace-wide tag list (with counts) for the tag-editor autocomplete.
  const knownTags = useSelector((s) => s.tasks.allTags);
  const filesMap = useSelector((s) =>
    dossierId ? s.files.files[dossierId] : undefined
  );
  const files = React.useMemo(
    () => (filesMap ? Object.values(filesMap) : []),
    [filesMap]
  );
  const onMentionClick = React.useCallback<MentionClickHandler>(
    (type, id) => (type === 'file' ? onFileClick(id) : onTaskClick(id)),
    [onFileClick, onTaskClick]
  );

  // Refetch on a successful comment so the new note shows in the thread.
  useEffectWithPrev(
    (prevStatus) => {
      if (
        prevStatus === 'pending' &&
        taskActions.commentOnTask.status === 'fulfilled'
      ) {
        mentionInputRef.current?.clear();
        handler.fetchTask();
      }
    },
    [taskActions.commentOnTask.status, handler]
  );

  const handleCitationClick = useCitationHandler(onFileClick, onKnowledgeClick);
  const { renumberedText, references } = React.useMemo(
    () => processCitations(task?.description ?? undefined),
    [task?.description]
  );

  if (!task && fetching) {
    return (
      <div className={styles.loading}>
        <Spin />
      </div>
    );
  }

  if (!task) {
    return <div className={styles.loading}>Task not found</div>;
  }

  const done = task.status === 'done';
  const raisedByOwl = task.raised_by === 'Owl';

  const commitTitle = () => {
    setEditingTitle(false);
    const trimmed = title.trim();
    if (trimmed && trimmed !== task.title) {
      handler.updateTask({ title: trimmed });
    } else {
      setTitle(task.title);
    }
  };

  const commitDescription = () => {
    setEditingDesc(false);
    if (description !== (task.description ?? '')) {
      handler.updateTask({ description });
    }
  };

  // Tag edits enqueue an atomic op; the tasks slice applies it optimistically
  // (so task.tags below is already current for dedupe) and a worker serializes
  // the server writes. Dedupe/guard against the optimistic tags.
  const addTag = (tag: string) => {
    const trimmed = tag.trim().toLowerCase();
    if (!trimmed || task.tags.some((t) => t.toLowerCase() === trimmed)) {
      return;
    }
    handler.enqueueTagOp({ kind: 'add', tag: trimmed });
  };

  const removeTag = (tag: string) => {
    const target = tag.toLowerCase();
    if (!task.tags.some((t) => t.toLowerCase() === target)) {
      return;
    }
    handler.enqueueTagOp({ kind: 'remove', tag: target });
  };

  const submitComment = () => {
    const serialized = mentionInputRef.current?.getValue() ?? '';
    const { text, mentions } = parseMentions(serialized);
    if (!text) {
      return;
    }
    handler.commentOnTask({ text, mentions, scope: 'claim' });
  };

  const openLink = (link: TaskLink) => {
    if (link.type === 'task') {
      onTaskClick(link.id);
    } else if (link.type === 'artifact') {
      onArtifactClick(link.id);
    } else {
      onFileClick(link.id);
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.scrollArea}>
        <div className={styles.contentWrap}>
          <div className={styles.mainColumn}>
            <div className={styles.titleBlock}>
              <div className={styles.titleRow}>
                {editingTitle ? (
                  <Input
                    autoFocus
                    className={styles.titleInput}
                    variant="borderless"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    onBlur={commitTitle}
                    onPressEnter={commitTitle}
                  />
                ) : (
                  <h1
                    className={clsx(styles.title, done && styles.titleDone)}
                    onClick={() => setEditingTitle(true)}
                  >
                    {task.title}
                  </h1>
                )}
              </div>
              <div className={styles.provenanceRow}>
                {raisedByOwl ? (
                  <span className={styles.owlChip}>
                    <RobotOutlined />
                    Raised by Owl
                  </span>
                ) : (
                  task.raised_by && (
                    <span className={styles.owlChip}>
                      <UserOutlined />
                      Raised by {task.raised_by}
                    </span>
                  )
                )}
                {task.kind === 'action' && (
                  <span className={styles.assistChip}>
                    <ThunderboltOutlined />
                    Owl can help
                  </span>
                )}
                <div className={styles.tagRow}>
                  {task.tags.map((tag) => (
                    <Tag
                      key={tag}
                      closable
                      onClose={(e) => {
                        e.preventDefault();
                        removeTag(tag);
                      }}
                    >
                      {tag}
                    </Tag>
                  ))}
                  <TagAutocomplete
                    known={knownTags}
                    existing={task.tags}
                    onAdd={addTag}
                  />
                </div>
              </div>
            </div>

            <div className={styles.section}>
              <div className={styles.sectionLabelRow}>
                <span className={styles.sectionLabel}>Description</span>
                {!editingDesc && task.description && (
                  <Button
                    type="text"
                    size="small"
                    className={styles.editBtn}
                    icon={<EditOutlined />}
                    onClick={() => setEditingDesc(true)}
                    aria-label="Edit description"
                  />
                )}
              </div>
              {editingDesc ? (
                <Input.TextArea
                  autoFocus
                  autoSize={{ minRows: 3, maxRows: 12 }}
                  className={styles.descInput}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  onBlur={commitDescription}
                />
              ) : task.description ? (
                // Rendered (not click-to-edit) so citation links stay clickable;
                // use the pencil above to edit.
                <div className={styles.body}>
                  <EscMarkdown
                    text={renumberedText || task.description || ''}
                    customCitationHandler={handleCitationClick}
                    onPageLink={onWikiClick}
                    hidePage
                    stateDb
                  />
                </div>
              ) : (
                <div
                  className={clsx(styles.body, styles.descEmpty)}
                  onClick={() => setEditingDesc(true)}
                >
                  Add a description…
                </div>
              )}
            </div>

            {task.attachments.length > 0 && (
              <div className={styles.section}>
                <div className={styles.sectionLabel}>Files</div>
                <div className={styles.attachments}>
                  {task.attachments.map((a) => (
                    <button
                      key={a.id}
                      type="button"
                      className={styles.attachment}
                      onClick={() => onFileClick(a.id)}
                    >
                      <FileTextOutlined />
                      <span>{a.name}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className={styles.section}>
              <div className={styles.sectionLabel}>
                Discussion
                <span className={styles.sectionCount}>
                  {' '}
                  {task.stream.length}
                </span>
              </div>
              <div className={styles.stream}>
                {task.stream.length ? (
                  task.stream.map((entry) => (
                    <DiscussionEntry
                      key={entry.id}
                      entry={entry}
                      files={files}
                      tasks={commentTasks}
                      onFileClick={onFileClick}
                      onKnowledgeClick={onKnowledgeClick}
                      onWikiClick={onWikiClick}
                      onMentionClick={onMentionClick}
                    />
                  ))
                ) : (
                  <div className={styles.discussionEmpty}>
                    Nothing yet. Add a claim note for the record.
                  </div>
                )}
              </div>
              <div className={styles.composer}>
                <div className={styles.composerField}>
                  <div className={styles.composerInput}>
                    <MentionInput
                      ref={mentionInputRef}
                      placeholder="Write a claim note — this joins the permanent record…"
                      disabled={commenting}
                      dossierId={dossierId ?? ''}
                      tasks={commentTasks}
                      onSend={submitComment}
                    />
                  </div>
                  <div className={styles.composerActions}>
                    <Button
                      type="primary"
                      shape="round"
                      size="small"
                      loading={commenting}
                      onClick={submitComment}
                    >
                      Add note
                    </Button>
                  </div>
                </div>
              </div>
            </div>

            {task.activity.length > 0 && (
              <div className={styles.section}>
                <div className={styles.sectionLabel}>
                  Activity
                  <span className={styles.sectionCount}>
                    {' '}
                    {task.activity.length}
                  </span>
                </div>
                <div className={styles.activity}>
                  {[...task.activity]
                    .sort(
                      (a, b) =>
                        dayjs.utc(b.ts).valueOf() - dayjs.utc(a.ts).valueOf()
                    )
                    .map((a) => (
                      <ActivityRow
                        key={a.id}
                        activity={a}
                        onFileClick={onFileClick}
                      />
                    ))}
                </div>
              </div>
            )}
          </div>

          <div className={styles.sidebar}>
            <div className={styles.metaBlock}>
              <div className={styles.metaLabel}>Status</div>
              <div className={styles.metaRow}>
                <span
                  className={clsx(
                    styles.statusPill,
                    done ? styles.statusDone : styles.statusOpen
                  )}
                >
                  {done ? 'Done' : 'Open'}
                </span>
                <Button
                  size="small"
                  type={done ? 'default' : 'primary'}
                  icon={<CheckOutlined />}
                  onClick={() =>
                    handler.updateTask({ status: done ? 'open' : 'done' })
                  }
                >
                  {done ? 'Reopen' : 'Mark complete'}
                </Button>
              </div>
            </div>

            {task.suggestions.length > 0 && (
              <div className={styles.metaBlock}>
                <div className={styles.metaLabel}>Owl can help with</div>
                <div className={styles.suggestionList}>
                  {task.suggestions.map((s, i) => {
                    const art = suggestionArtifact(s.schema_name);
                    const status = art ? artifactStatus(art) : null;
                    const isDone = status === 'ready';
                    const isGenerating =
                      !isDone &&
                      (generating.has(s.schema_name) ||
                        status === 'processing');
                    return (
                      <button
                        key={i}
                        type="button"
                        className={styles.suggestionRow}
                        disabled={isGenerating}
                        onClick={
                          isDone && art
                            ? () => onArtifactClick(art.artifact_id)
                            : () => runSuggestion(s)
                        }
                      >
                        <span className={styles.suggestionIcon}>
                          {isGenerating ? (
                            <LoadingOutlined />
                          ) : isDone ? (
                            <CheckCircleOutlined />
                          ) : (
                            <FormOutlined />
                          )}
                        </span>
                        <span className={styles.suggestionLabel}>
                          {s.label}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {task.blocked_by.length > 0 && (
              <div className={styles.metaBlock}>
                <div className={styles.metaLabel}>Blocked by</div>
                <div className={styles.blockerList}>
                  {task.blocked_by.map((id) => {
                    const b = commentTasks.find((t) => t.id === id);
                    if (!b) {
                      return null;
                    }
                    const bDone = b.status === 'done';
                    return (
                      <button
                        key={id}
                        type="button"
                        className={clsx(
                          styles.blockerChip,
                          bDone && styles.blockerDone
                        )}
                        onClick={() => onTaskClick(id)}
                      >
                        {bDone ? (
                          <CheckCircleOutlined />
                        ) : (
                          <ClockCircleOutlined />
                        )}
                        <span className={styles.blockerTitle}>{b.title}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            <div className={styles.metaBlock}>
              <div className={styles.metaLabel}>Priority</div>
              <Select
                className={styles.metaControl}
                value={task.priority}
                onChange={(p) =>
                  handler.updateTask({ priority: p as TaskPriority })
                }
                options={PRIORITY_OPTIONS}
              />
            </div>

            <div className={styles.metaBlock}>
              <div className={styles.metaLabel}>Due date</div>
              <DatePicker
                className={styles.metaControl}
                value={task.due ? dayjs(task.due) : null}
                onChange={(d) =>
                  handler.updateTask({
                    due: d ? d.format('YYYY-MM-DD') : null,
                  })
                }
                format="MMM D, YYYY"
                placeholder="No due date"
                suffixIcon={<CalendarOutlined />}
              />
            </div>

            <div className={styles.metaBlock}>
              <div className={styles.metaLabel}>Assignee</div>
              <Select
                className={styles.metaControl}
                showSearch
                allowClear
                placeholder="Unassigned"
                value={task.assignee || undefined}
                options={roster.map((n) => ({ label: n, value: n }))}
                onChange={(val) =>
                  handler.updateTask({ assignee: val ?? null })
                }
                optionFilterProp="label"
                suffixIcon={<UserOutlined />}
              />
            </div>

            {task.link && (
              <div className={styles.metaBlock}>
                <div className={styles.metaLabel}>Linked record</div>
                <button
                  type="button"
                  className={styles.linkChip}
                  title={task.link.label ?? task.link.type}
                  onClick={() => task.link && openLink(task.link)}
                >
                  <span className={styles.linkChipIcon}>
                    {LINK_ICON[task.link.type]}
                  </span>
                  <span className={styles.linkChipLabel}>
                    {task.link.label ?? task.link.type}
                  </span>
                </button>
              </div>
            )}

            {references.length > 0 && (
              <ReferencesCard
                references={references}
                onFileClick={onFileClick}
                onKnowledgeClick={onKnowledgeClick}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TaskDetail;
